import React from 'react';
import { DocsLayout } from '../../components/docs/DocsLayout';
import { CodeBlock } from '../../components/docs/CodeBlock';
import { Callout } from '../../components/docs/Callout';
import { ArrowRight, ExternalLink } from 'lucide-react';
export function OrchestrateAPIPage() {
  const createExecutionCode = {
    curl: `curl -X POST https://api.obseq.com/v1/orchestrate/executions \\
  -H "Authorization: Bearer sk_live_..." \\
  -H "Content-Type: application/json" \\
  -H "Idempotency-Key: $(uuidgen)" \\
  -d '{
    "intent": "pay_supplier",
    "amount": 250000,
    "currency": "USD",
    "destination": {
      "beneficiary_id": "bn_123"
    },
    "metadata": {
      "invoice_id": "INV-10291"
    }
  }'`,
    node: `const execution = await fetch('https://api.obseq.com/v1/orchestrate/executions', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${apiKey}\`,
    'Content-Type': 'application/json',
    'Idempotency-Key': crypto.randomUUID()
  },
  body: JSON.stringify({
    intent: 'pay_supplier',
    amount: 250000,
    currency: 'USD',
    destination: {
      beneficiary_id: 'bn_123'
    },
    metadata: {
      invoice_id: 'INV-10291'
    }
  })
});

const data = await execution.json();`,
    python: `import uuid, requests

execution = requests.post(
    'https://api.obseq.com/v1/orchestrate/executions',
    headers={
        'Authorization': f'Bearer {api_key}',
        'Idempotency-Key': str(uuid.uuid4())
    },
    json={
        'intent': 'pay_supplier',
        'amount': 250000,
        'currency': 'USD',
        'destination': {
            'beneficiary_id': 'bn_123'
        },
        'metadata': {
            'invoice_id': 'INV-10291'
        }
    }
)`,
    go: `execution := map[string]interface{}{
    "intent": "pay_supplier",
    "amount": 250000,
    "currency": "USD",
    "destination": map[string]string{
        "beneficiary_id": "bn_123",
    },
}

body, _ := json.Marshal(execution)
req, _ := http.NewRequest("POST", "https://api.obseq.com/v1/orchestrate/executions", bytes.NewBuffer(body))
req.Header.Set("Authorization", "Bearer "+apiKey)
req.Header.Set("Idempotency-Key", uuid.New().String())`
  };
  return <DocsLayout breadcrumbs={[{
    label: 'Docs',
    href: '/docs'
  }, {
    label: 'API Reference',
    href: '/docs/api-reference'
  }, {
    label: 'Orchestrate',
    href: '/docs/api-reference/orchestrate'
  }]} onThisPage={[{
    title: 'Overview',
    id: 'overview'
  }, {
    title: 'Resources',
    id: 'resources'
  }, {
    title: 'Endpoints',
    id: 'endpoints'
  }, {
    title: 'Create Execution',
    id: 'create-execution'
  }, {
    title: 'Webhooks',
    id: 'webhooks'
  }, {
    title: 'Errors',
    id: 'errors'
  }]} data-id="element-1617">
      <h1 data-id="element-1618">Orchestrate API</h1>
      <p className="lead" data-id="element-1619">
        Orchestrate is the decision and execution layer that routes intents
        (e.g., "pay supplier in NGN") across Payments, Exchange, Liquidity,
        Compliance, and Wallets with policy controls.
      </p>

      <div className="flex gap-3 my-6" data-id="element-1620">
        <a href="#/docs/guides/getting-started" className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-1621">
          Try in Sandbox
          <ExternalLink className="w-4 h-4" data-id="element-1622" />
        </a>
      </div>

      <h2 id="overview" data-id="element-1623">Overview</h2>
      <p data-id="element-1624">
        Orchestrate provides intelligent routing and execution for complex money
        operations. Define rules, create intents, and let Orchestrate handle the
        coordination across multiple APIs.
      </p>

      <h2 id="resources" data-id="element-1625">Key Resources</h2>
      <table className="w-full text-sm my-6" data-id="element-1626">
        <thead data-id="element-1627">
          <tr className="border-b border-slate-200" data-id="element-1628">
            <th className="text-left py-2 pr-4" data-id="element-1629">Resource</th>
            <th className="text-left py-2 pr-4" data-id="element-1630">Object</th>
            <th className="text-left py-2" data-id="element-1631">Status Values</th>
          </tr>
        </thead>
        <tbody data-id="element-1632">
          <tr className="border-b border-slate-100" data-id="element-1633">
            <td className="py-3 pr-4" data-id="element-1634">
              <code data-id="element-1635">execution</code>
            </td>
            <td className="py-3 pr-4" data-id="element-1636">
              <code data-id="element-1637">orchestrate.execution</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1638">
              created, pending, completed, failed, canceled
            </td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1639">
            <td className="py-3 pr-4" data-id="element-1640">
              <code data-id="element-1641">route</code>
            </td>
            <td className="py-3 pr-4" data-id="element-1642">
              <code data-id="element-1643">orchestrate.route</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1644">active, inactive</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1645">
            <td className="py-3 pr-4" data-id="element-1646">
              <code data-id="element-1647">rule</code>
            </td>
            <td className="py-3 pr-4" data-id="element-1648">
              <code data-id="element-1649">orchestrate.rule</code>
            </td>
            <td className="py-3 text-slate-600" data-id="element-1650">active, inactive</td>
          </tr>
        </tbody>
      </table>

      <h3 data-id="element-1651">Status Lifecycle</h3>
      <div className="my-6 bg-slate-50 border border-slate-200 rounded-lg p-6" data-id="element-1652">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-1653">
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-1654">
            <span className="text-sm font-semibold text-blue-900" data-id="element-1655">created</span>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 rotate-90 md:rotate-0" data-id="element-1656" />
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-1657">
            <span className="text-sm font-semibold text-yellow-900" data-id="element-1658">
              pending
            </span>
          </div>
          <ArrowRight className="w-4 h-4 text-slate-400 rotate-90 md:rotate-0" data-id="element-1659" />
          <div className="flex flex-col gap-2" data-id="element-1660">
            <div className="bg-green-50 border-2 border-green-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-1661">
              <span className="text-sm font-semibold text-green-900" data-id="element-1662">
                completed
              </span>
            </div>
            <div className="bg-red-50 border-2 border-red-200 rounded-lg px-4 py-2 text-center min-w-[100px]" data-id="element-1663">
              <span className="text-sm font-semibold text-red-900" data-id="element-1664">failed</span>
            </div>
          </div>
        </div>
      </div>

      <h2 id="endpoints" data-id="element-1665">Endpoint Index</h2>

      <h3 data-id="element-1666">Routes</h3>
      <ul className="space-y-2 my-4" data-id="element-1667">
        <li data-id="element-1668">
          <code className="text-sm" data-id="element-1669">GET /v1/orchestrate/routes</code> - List
          available route templates
        </li>
        <li data-id="element-1670">
          <code className="text-sm" data-id="element-1671">GET /v1/orchestrate/routes/:id</code> -
          Retrieve route details
        </li>
      </ul>

      <h3 data-id="element-1672">Rules</h3>
      <ul className="space-y-2 my-4" data-id="element-1673">
        <li data-id="element-1674">
          <code className="text-sm" data-id="element-1675">GET /v1/orchestrate/rules</code> - List
          orchestration rules
        </li>
        <li data-id="element-1676">
          <code className="text-sm" data-id="element-1677">POST /v1/orchestrate/rules</code> - Create
          orchestration rule
        </li>
        <li data-id="element-1678">
          <code className="text-sm" data-id="element-1679">PUT /v1/orchestrate/rules/:id</code> -
          Update orchestration rule
        </li>
      </ul>

      <h3 data-id="element-1680">Executions</h3>
      <ul className="space-y-2 my-4" data-id="element-1681">
        <li data-id="element-1682">
          <code className="text-sm" data-id="element-1683">POST /v1/orchestrate/intents</code> - Create
          intent
        </li>
        <li data-id="element-1684">
          <code className="text-sm" data-id="element-1685">POST /v1/orchestrate/executions</code> -
          Execute intent
        </li>
        <li data-id="element-1686">
          <code className="text-sm" data-id="element-1687">GET /v1/orchestrate/executions/:id</code> -
          Retrieve execution
        </li>
        <li data-id="element-1688">
          <code className="text-sm" data-id="element-1689">
            POST /v1/orchestrate/executions/:id/cancel
          </code>{' '}
          - Cancel execution
        </li>
        <li data-id="element-1690">
          <code className="text-sm" data-id="element-1691">
            GET /v1/orchestrate/executions/:id/timeline
          </code>{' '}
          - Get execution timeline
        </li>
      </ul>

      <h2 id="create-execution" data-id="element-1692">Create Execution</h2>
      <p data-id="element-1693">Execute an intent with orchestration logic.</p>

      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4" data-id="element-1694">
        <code className="text-sm font-semibold" data-id="element-1695">
          POST /v1/orchestrate/executions
        </code>
      </div>

      <h3 data-id="element-1696">Parameters</h3>
      <table className="w-full text-sm my-4" data-id="element-1697">
        <thead data-id="element-1698">
          <tr className="border-b border-slate-200" data-id="element-1699">
            <th className="text-left py-2 pr-4" data-id="element-1700">Parameter</th>
            <th className="text-left py-2 pr-4" data-id="element-1701">Type</th>
            <th className="text-left py-2 pr-4" data-id="element-1702">Required</th>
            <th className="text-left py-2" data-id="element-1703">Description</th>
          </tr>
        </thead>
        <tbody data-id="element-1704">
          <tr className="border-b border-slate-100" data-id="element-1705">
            <td className="py-2 pr-4" data-id="element-1706">
              <code data-id="element-1707">intent</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1708">string</td>
            <td className="py-2 pr-4" data-id="element-1709">Yes</td>
            <td className="py-2" data-id="element-1710">Intent type (e.g., "pay_supplier")</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1711">
            <td className="py-2 pr-4" data-id="element-1712">
              <code data-id="element-1713">amount</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1714">integer</td>
            <td className="py-2 pr-4" data-id="element-1715">Yes</td>
            <td className="py-2" data-id="element-1716">Amount in minor units</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1717">
            <td className="py-2 pr-4" data-id="element-1718">
              <code data-id="element-1719">currency</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1720">string</td>
            <td className="py-2 pr-4" data-id="element-1721">Yes</td>
            <td className="py-2" data-id="element-1722">Three-letter ISO currency code</td>
          </tr>
          <tr className="border-b border-slate-100" data-id="element-1723">
            <td className="py-2 pr-4" data-id="element-1724">
              <code data-id="element-1725">destination</code>
            </td>
            <td className="py-2 pr-4" data-id="element-1726">object</td>
            <td className="py-2 pr-4" data-id="element-1727">Yes</td>
            <td className="py-2" data-id="element-1728">Destination details</td>
          </tr>
        </tbody>
      </table>

      <h3 data-id="element-1729">Example Request</h3>
      <CodeBlock code={createExecutionCode} data-id="element-1730" />

      <h3 data-id="element-1731">Example Response</h3>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-1732">
        <code data-id="element-1733">{`{
  "id": "ex_01J9Z98K5K8C0MZ9QK3G4B1E2P",
  "object": "orchestrate.execution",
  "status": "pending",
  "intent": "pay_supplier",
  "amount": 250000,
  "currency": "USD",
  "created_at": "2026-01-03T18:41:22Z"
}`}</code>
      </pre>

      <h2 id="webhooks" data-id="element-1734">Webhook Events</h2>
      <ul className="space-y-2 my-4" data-id="element-1735">
        <li data-id="element-1736">
          <code data-id="element-1737">orchestrate.execution.created</code> - Execution initiated
        </li>
        <li data-id="element-1738">
          <code data-id="element-1739">orchestrate.execution.updated</code> - Execution status changed
        </li>
        <li data-id="element-1740">
          <code data-id="element-1741">orchestrate.execution.completed</code> - Execution completed
          successfully
        </li>
        <li data-id="element-1742">
          <code data-id="element-1743">orchestrate.execution.failed</code> - Execution failed
        </li>
      </ul>

      <h3 data-id="element-1744">Example Event</h3>
      <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto my-4" data-id="element-1745">
        <code data-id="element-1746">{`{
  "id": "evt_123",
  "type": "orchestrate.execution.completed",
  "created_at": "2026-01-03T18:41:22Z",
  "data": {
    "object": {
      "id": "ex_123",
      "status": "completed"
    }
  }
}`}</code>
      </pre>

      <h2 id="errors" data-id="element-1747">Common Errors</h2>
      <ul className="space-y-2 my-4" data-id="element-1748">
        <li data-id="element-1749">
          <code data-id="element-1750">invalid_intent</code> - Intent type not recognized
        </li>
        <li data-id="element-1751">
          <code data-id="element-1752">routing_failed</code> - No route found for intent
        </li>
        <li data-id="element-1753">
          <code data-id="element-1754">execution_failed</code> - Execution could not complete
        </li>
        <li data-id="element-1755">
          <code data-id="element-1756">insufficient_balance</code> - Not enough funds
        </li>
      </ul>

      <Callout type="note" data-id="element-1757">
        See{' '}
        <a href="#/docs/guides/errors" className="text-blue-600 hover:text-blue-700" data-id="element-1758">
          Error Handling
        </a>{' '}
        for complete error reference.
      </Callout>

      <div className="mt-12 pt-8 border-t border-slate-200" data-id="element-1759">
        <h3 className="text-lg font-semibold text-slate-900 mb-4" data-id="element-1760">
          Related Guides
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1761">
          <a href="#/docs/guides/authentication" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1762">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1763">
              Authentication
            </h4>
            <p className="text-sm text-slate-600" data-id="element-1764">API key setup</p>
          </a>
          <a href="#/docs/guides/webhooks" className="p-4 bg-slate-50 border border-slate-200 rounded-lg hover:border-blue-300 transition-colors" data-id="element-1765">
            <h4 className="font-semibold text-slate-900 mb-1" data-id="element-1766">Webhooks</h4>
            <p className="text-sm text-slate-600" data-id="element-1767">Handle execution events</p>
          </a>
        </div>
      </div>
    </DocsLayout>;
}